import { useState, useEffect, useCallback, useRef } from 'react';
import { useLang } from '@/hooks/useLang';

const BASE = 'https://www.yamamoto-ss.co.jp/sp_recycle';

const slides = [
  {
    img: `${BASE}/_resources/img_top/slide_melter_re-e502.png`,
    bgImg: 'https://public.readdy.ai/ai/img_res/7575e81d3b5b123d4c339bcecf81e38c.jpg',
    tagEn: 'EPS Compactor Series',
    tagJa: '発泡スチロール減容機シリーズ',
    titleEn: 'RE-E502',
    titleJa: 'RE-E502',
    subEn: 'High-throughput EPS foam densifier for North American recyclers',
    subJa: '北米リサイクル業者向け高処理能力発泡スチロール減容機',
    href: `${BASE}/products/melter/re-e502.html`,
  },
  {
    img: `${BASE}/_resources/img_top/slide_melter_re-e2000.png`,
    bgImg: 'https://readdy.ai/api/search-image?query=large%20industrial%20warehouse%20dark%20moody%20lighting%20heavy%20equipment%20manufacturing%20plant%20dramatic%20shadows%20steel%20concrete%20cinematic%20industrial%20atmosphere&width=1600&height=900&seq=hero2&orientation=landscape',
    tagEn: 'Large-Scale EPS Compactor',
    tagJa: '大型発泡スチロール減容機',
    titleEn: 'RE-E2000',
    titleJa: 'RE-E2000',
    subEn: 'Industrial-grade densification for high-volume EPS waste streams',
    subJa: '大量の発泡スチロール廃棄物に対応する産業用減容機',
    href: `${BASE}/products/melter/re-e1000a-3000.html`,
  },
  {
    img: `${BASE}/_resources/img_top/slide_pet_pls-5000.png`,
    bgImg: 'https://readdy.ai/api/search-image?query=recycling%20sorting%20facility%20conveyor%20belt%20dark%20industrial%20dramatic%20lighting%20plastic%20bottles%20processing%20plant%20cinematic%20moody%20atmosphere%20deep%20blue%20tones&width=1600&height=900&seq=hero3&orientation=landscape',
    tagEn: 'PET Bottle Label Remover',
    tagJa: 'ペットボトルラベル分離機',
    titleEn: 'PLS-5000',
    titleJa: 'PLS-5000',
    subEn: 'Automated label separation for high-purity PET recycling',
    subJa: '高純度PETリサイクルのための自動ラベル分離',
    href: `${BASE}/products/pet/pls-5000.html`,
  },
  {
    img: `${BASE}/_resources/img_top/slide_grush_fg-501bw.png`,
    bgImg: 'https://readdy.ai/api/search-image?query=plastic%20granule%20processing%20machine%20dark%20industrial%20setting%20dramatic%20lighting%20manufacturing%20floor%20cinematic%20moody%20atmosphere%20steel%20machinery%20close%20up&width=1600&height=900&seq=hero4&orientation=landscape',
    tagEn: 'Granule Compactor',
    tagJa: '造粒減容機',
    titleEn: 'FG-501BW',
    titleJa: 'FG-501BW',
    subEn: 'Precision granulation for clean, market-ready recycled material',
    subJa: '市場品質のリサイクル原料を生み出す精密造粒機',
    href: `${BASE}/products/grush/fg-501bw.html`,
  },
];

export default function HeroSlider() {
  const { lang, t } = useLang();
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((idx: number) => {
    if (transitioning) return;
    setPrev(current);
    setTransitioning(true);
    setTimeout(() => {
      setCurrent(idx);
      setPrev(null);
      setTransitioning(false);
    }, 700);
  }, [current, transitioning]);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prevSlide = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, 6000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [next]);

  const slide = slides[current];

  return (
    <div className="relative w-full h-[100vh] min-h-[640px] max-h-[900px] overflow-hidden bg-[#0a0a0a]">
      {/* Background layers */}
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0, zIndex: i === current ? 1 : 0 }}
        >
          <img
            src={s.bgImg}
            alt=""
            className="w-full h-full object-cover object-center"
            style={{ filter: 'brightness(0.35) saturate(0.8)' }}
          />
        </div>
      ))}

      {/* Gradient overlays */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/80 via-black/40 to-transparent pointer-events-none"></div>
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none"></div>

      {/* Red accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#c0392b] z-20"></div>

      {/* Product image */}
      <div
        className="absolute right-0 bottom-0 h-[85%] w-[50%] flex items-end justify-center z-20 pointer-events-none"
        style={{ transition: 'opacity 0.7s', opacity: transitioning ? 0 : 1 }}
      >
        <img
          src={slide.img}
          alt={lang === 'en' ? slide.titleEn : slide.titleJa}
          className="h-full w-auto object-contain object-bottom drop-shadow-2xl"
          style={{ filter: 'drop-shadow(0 0 60px rgba(192,57,43,0.3))' }}
        />
      </div>

      {/* Content */}
      <div
        className="absolute left-0 top-0 h-full flex flex-col justify-center pl-12 md:pl-20 z-30 max-w-2xl"
        style={{ transition: 'opacity 0.7s, transform 0.7s', opacity: transitioning ? 0 : 1, transform: transitioning ? 'translateY(20px)' : 'translateY(0)' }}
      >
        {/* Tag */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px bg-[#c0392b]"></div>
          <span className="text-[#c0392b] text-xs font-bold uppercase tracking-[0.2em]">
            {lang === 'en' ? slide.tagEn : slide.tagJa}
          </span>
        </div>

        {/* Main title */}
        <h1 className="text-6xl md:text-8xl font-black text-white leading-none mb-4 tracking-tight">
          {lang === 'en' ? slide.titleEn : slide.titleJa}
        </h1>

        {/* Subtitle */}
        <p className="text-white/70 text-sm md:text-base leading-relaxed mb-8 max-w-md">
          {lang === 'en' ? slide.subEn : slide.subJa}
        </p>

        {/* CTA */}
        <div className="flex items-center gap-4">
          <a
            href={slide.href}
            className="group flex items-center gap-3 bg-[#c0392b] text-white text-sm font-bold px-7 py-3.5 hover:bg-[#a93226] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('View Product', '製品を見る')}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>
          <a
            href={`${BASE}/contact/`}
            className="flex items-center gap-2 text-white/80 text-sm font-medium hover:text-white transition-colors whitespace-nowrap cursor-pointer border-b border-white/30 hover:border-white pb-0.5"
          >
            {t('Request a Quote', 'お問い合わせ')}
          </a>
        </div>

        {/* Slide counter */}
        <div className="flex items-center gap-3 mt-12">
          <span className="text-[#c0392b] text-2xl font-black tabular-nums">
            {String(current + 1).padStart(2, '0')}
          </span>
          <div className="flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="cursor-pointer transition-all duration-300"
                aria-label={`Slide ${i + 1}`}
              >
                <div className={`h-0.5 transition-all duration-500 ${i === current ? 'w-8 bg-[#c0392b]' : 'w-4 bg-white/30 hover:bg-white/60'}`}></div>
              </button>
            ))}
          </div>
          <span className="text-white/30 text-sm tabular-nums">
            {String(slides.length).padStart(2, '0')}
          </span>
        </div>
      </div>

      {/* Nav arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-6 bottom-10 z-30 w-11 h-11 flex items-center justify-center border border-white/20 text-white/60 hover:border-[#c0392b] hover:text-[#c0392b] transition-all cursor-pointer"
        aria-label="Previous"
      >
        <i className="ri-arrow-left-line"></i>
      </button>
      <button
        onClick={next}
        className="absolute left-20 bottom-10 z-30 w-11 h-11 flex items-center justify-center border border-white/20 text-white/60 hover:border-[#c0392b] hover:text-[#c0392b] transition-all cursor-pointer"
        aria-label="Next"
      >
        <i className="ri-arrow-right-line"></i>
      </button>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 right-10 z-30 flex flex-col items-center gap-2 text-white/30">
        <span className="text-[10px] uppercase tracking-widest" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/30 to-transparent"></div>
      </div>
    </div>
  );
}
